import React from "react";
import { Outlet, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useQueryClient } from "@tanstack/react-query";
import { usePortalProjects, usePortalProject } from "@/features/portal/api/portal-api";
import {
  applyPortalDirection,
  resetPortalDirection,
  usePortalLanguage,
} from "@/features/portal/store/usePortalLanguage";
import PortalTopBar from "./portal-top-bar";
import PortalTabBar from "./portal-tab-bar";
import PortalErrorFallback from "./portal-error-fallback";
import { usePortalPendingChangeOrderCount } from "@/features/portal/api/portal-api";

type SafeProject = {
  id: string;
  name: string;
  status: string;
  startDate: string | null;
  estimatedEndDate: string | null;
  totalBudget: string;
  progress: number;
  decisionCount: number;
};

const PortalLayout = ({ children }: { children?: React.ReactNode }) => {
  const { projectId } = useParams();
  const language = usePortalLanguage((state) => state.language);
  const queryClient = useQueryClient();

  const { data: projectsRes, isLoading: projectsLoading, isError: projectsError } =
    usePortalProjects();
  const projects: SafeProject[] = projectsRes?.data ?? [];

  const activeProjectId = projectId ?? projects[0]?.id;
  const { data: projectRes, isLoading: projectLoading, isError: projectError } =
    usePortalProject(projectId);
  const activeProject = projectRes?.data as SafeProject | undefined;
  const { data: pendingCoData } = usePortalPendingChangeOrderCount(activeProjectId);
  const pendingChangeOrderCount = pendingCoData ?? 0;

  const handleReload = () => {
    queryClient.invalidateQueries({ queryKey: ["portal", "projects"] });
    if (activeProjectId) {
      queryClient.invalidateQueries({
        queryKey: ["portal", "project", activeProjectId],
      });
    }
  };

  // Index route (no projectId) renders a project picker; the single-project
  // case is handled declaratively there via <Navigate>. No redirect effect here.

  // Apply RTL when entering the portal, restore LTR when leaving.
  React.useEffect(() => {
    applyPortalDirection(language);
    return () => resetPortalDirection();
  }, [language]);

  const isLoading = (projectsLoading && !activeProjectId) || projectLoading;
  const hasError = projectsError || projectError;

  return (
    <div className="min-h-screen flex flex-col bg-(--color-bg-page)">
      <PortalTopBar
        projects={projects}
        activeProjectId={activeProjectId}
        activeProjectName={activeProject?.name}
      />
      <PortalTabBar
        activeProjectId={activeProjectId}
        decisionCount={activeProject?.decisionCount ?? 0}
        changeOrderCount={pendingChangeOrderCount}
      />
      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-300 mx-auto">
          {hasError ? (
            <PortalErrorFallback onReload={handleReload} />
          ) : isLoading ? (
            <div className="py-20 text-center text-(--color-text-tertiary)">
              Loading…
            </div>
          ) : (
            <ErrorBoundary
              FallbackComponent={(props) => (
                <PortalErrorFallback
                  message={
                    props.error instanceof Error
                      ? props.error.message
                      : "We couldn't load this content. Please try again."
                  }
                  onReload={handleReload}
                />
              )}
            >
              {children ?? <Outlet />}
            </ErrorBoundary>
          )}
        </div>
      </main>
    </div>
  );
};

export default PortalLayout;
